import type { Flow } from '@deity-io/di-data-flows';
import type { mxCell } from '@deity-io/mxgraph';
import type { IConnectPayload } from 'contexts/FlowContext';
import type { IPoint } from 'contracts/interfaces';
import _ from 'lodash';

export type IEntityRepo = {
  endpoints: Record<string, any>;
  models: Record<string, any>;
  mappings: Record<string, any>;
  flows: Record<string, any>;
};
export const COMP_ID_TRIGGER = '@trigger';

export const isTriggerComponent = (key: string): boolean => key === COMP_ID_TRIGGER;

export const isVirtualComponent = (
  cell: mxCell
): cell is mxCell & { isVirtual: true; virtualComponentType: 'trigger' | 'tail' } => cell['isVirtual'] === true;

export const canConnect = (flow: Flow, payload: IConnectPayload): [boolean, Array<string>] => {
  const { from, to } = payload;
  if (isTriggerComponent(from.compId)) {
    // @todo validate to schema when available
    return [true, []];
  }
  const fromComp = flow.getComponent(from.compId)!;
  const toComp = flow.getComponent(to.compId)!;

  return flow.canConnect(fromComp, from.portKey, toComp, to.portKey);
};

export const connectPayload = (source: mxCell, target: mxCell): IConnectPayload | undefined => {
  const getIdAndPort = (vertex: mxCell) => {
    if (vertex.parent.value.compId && vertex.value.key) {
      return {
        compId: vertex.parent.value.compId,
        portKey: vertex.value.key
      };
    }

    return undefined;
  };
  const payload = {
    from: getIdAndPort(source),
    to: getIdAndPort(target)
  };

  if (_.isUndefined(payload.from) || _.isUndefined(payload.from)) {
    return undefined;
  }

  return payload as IConnectPayload;
};
/**
 * returns the data schema for a output port and the data requirements for an input port
 */
export const getComponentDataSchema = (flow: Flow, compId: string, port: string, messages: Array<string> = []) => {
  const comp = flow.getComponent(compId);
  if (comp) {
    const definition = flow.getComponentDefinition(comp); // must use this func to get the definition. ports might be depended on flow
    if (port in definition.ports) {
      if (comp.isInputPort(definition.ports[port])) {
        const portDef = comp.getInputPort(port, definition);
        if (!portDef) {
          // eslint-disable-next-line no-console
          console.warn(`Port ${port} not found on component ${compId}`);

          return undefined;
        }

        return {
          direction: 'input',
          requirements: flow.getDataRequirements(comp, portDef, messages)
        };
      }

      const portDef = comp.getOutputPort(port, definition);
      if (!portDef) {
        // eslint-disable-next-line no-console
        console.warn(`Port ${port} not found on component ${compId}`);

        return undefined;
      }

      // else its a output port
      return { direction: 'output', schema: flow.getDataSchema(comp, portDef, messages) };
    }
  } else {
    const flowPorts = flow.getFlowPorts();
    if (compId in flowPorts) {
      const output = { direction: 'input', requirements: flow.getDataRequirementsForFlowPort(compId, messages) };

      return output;
    }
    if (compId === '@trigger') {
      /**
       * @todo Handle al types of virtual components, not just the trigger
       */
      const output = { direction: 'output', schema: flow.getDataSchemaForTrigger(messages) };

      return output;
    }
  }
};

/**
 * GEt the bounding rectangle around the components in a flow
 */
export const getComponentBounds = (flow: Flow, margin = 50): { upperLeft: IPoint; lowerRight: IPoint } => {
  const max = Number.MAX_SAFE_INTEGER;
  const bounds = { upperLeft: { x: max, y: max }, lowerRight: { x: 0, y: 0 } };

  flow?.filterFlow(flow, comp => {
    const { editor } = comp.config;
    const def = comp.definition;
    if (editor) {
      if (editor.location) {
        if (editor.location.x < bounds.upperLeft.x) {
          bounds.upperLeft.x = editor.location.x;
        }
        if (editor.location.y < bounds.upperLeft.y) {
          bounds.upperLeft.y = editor.location.y;
        }
        /**
         * @todo add size, and margin
         */
        const x = editor.location.x + (editor.size?.width || def.dimensions.width);
        const y = editor.location.y + (editor.size?.height || def.dimensions.height);
        if (x > bounds.lowerRight.x) {
          bounds.lowerRight.x = x;
        }
        if (y > bounds.lowerRight.y) {
          bounds.lowerRight.y = y;
        }
      }
    }

    return undefined;
  });
  // add border
  if (bounds.upperLeft.x > margin) {
    bounds.upperLeft.x -= margin;
  }

  bounds.upperLeft.y -= margin;

  bounds.lowerRight.x += margin;
  bounds.lowerRight.y += margin;

  return bounds;
};
